import { PhotoResponseDTO } from "@/api/PSApiOperations"
import { StyleReferenceRecord } from "@/lib/catalog"
import { useReferenceImageUrl } from "@/store/style/useReferenceImageUrl"
import { Image } from "../ui/Image"
import clsx from "clsx"
import { middleTruncate } from "@/utils/string"
import { useMemo } from "react"
import * as Tooltip from "@radix-ui/react-tooltip"

export const isImplicit = (value: StyleReferenceRecord | undefined | null): boolean => {
	if (!value) {
		return false
	}

	if (typeof value.isImplicit === "boolean") {
		return value.isImplicit
	} else {
		return !!value.isImplicit
	}
}

export const StyleReferenceImage = (props: {
	reference?: StyleReferenceRecord
	remotePhoto?: PhotoResponseDTO
	imageData?: Blob
	localPhotoId?: string
	aspectFit?: boolean
	growToFit?: boolean
	onSelected?: (imageId: string | undefined) => boolean
	isSelected?: boolean
	backgroundBaseColor?: string
	showFileName?: boolean
}) => {
	const url = useReferenceImageUrl(props)
	const imageId = props.reference?.imageInfo?.imageId
	const imageFileName = typeof imageId === "string" ? (imageId.split("|")[0] ?? "") : ""
	const displayFileName = useMemo(() => {
		return middleTruncate(imageFileName, 15)
	}, [imageFileName])

	const shouldShowFileName = props.showFileName ?? false
	const shouldShowTooltip = shouldShowFileName && displayFileName.length !== imageFileName.length
	const backgroundColor = props.backgroundBaseColor
		? `bg-${props.backgroundBaseColor}`
		: "bg-base-800"

	if (!url) {
		return null
	}

	return (
		<div
			className={clsx(
				"relative aspect-square overflow-hidden border-2",
				backgroundColor,
				props.growToFit ? "w-full" : "size-28",
				props.isSelected
					? "border-accent-600"
					: props.backgroundBaseColor
						? `border-${props.backgroundBaseColor}`
						: "bg-base-800",
			)}
			onClick={() => {
				if (props.onSelected) {
					props.onSelected(props.reference?.imageId)
				}
			}}
		>
			<Image
				animateWhileLoading
				className={clsx(
					"aspect-square size-full",
					props.aspectFit ? "object-contain" : "object-cover",
				)}
				key={url}
				src={url}
				draggable={false}
			/>

			{shouldShowFileName && (
				<Tooltip.Provider delayDuration={0}>
					<Tooltip.Root open={shouldShowTooltip ? undefined : false}>
						<Tooltip.Trigger asChild>
							<div
								className={clsx(
									"absolute bottom-0 w-full px-1 py-0.5 text-center",
									`${backgroundColor}/50`,
								)}
								onPointerEnter={(e) => e.stopPropagation()}
								onPointerLeave={(e) => e.stopPropagation()}
								onClick={(e) => e.stopPropagation()}
							>
								<span className="overflow-hidden whitespace-nowrap text-xs text-base-200">
									{displayFileName}
								</span>
							</div>
						</Tooltip.Trigger>
						{shouldShowTooltip && (
							<Tooltip.Portal>
								<Tooltip.Content
									side="top"
									sideOffset={8}
									className="rounded-lg border border-base-700 bg-base-800 p-2 text-xs text-base-200 shadow-lg"
									onPointerEnter={(e) => e.stopPropagation()}
									onPointerLeave={(e) => e.stopPropagation()}
								>
									{imageFileName}
								</Tooltip.Content>
							</Tooltip.Portal>
						)}
					</Tooltip.Root>
				</Tooltip.Provider>
			)}
		</div>
	)
}
